// store.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import axios from "axios";

export const useAuthStore = create(
  persist(
    (set) => ({
  email: "",
  name: "",
  lastName: "",
  phone: "",
  isAuthenticated: false,
  emailVerified: false,
  userId: "",
  isAdmin: false,
  shouldSeeAdminPanel: false,
  authInitialized: false,
  lessons: [],
  passes: [],
  user: null,
  roles: [],
  isTrainer: false,
  isExternalTrainer: false,
  trainerLessonTypes: [],
  pendingSignups: { count: 0, total_amount: 0, online: { count: 0, total_amount: 0, lessons: [] }, on_site: { count: 0, total_amount: 0, lessons: [] }, due: { online_count: 0, online_total: 0, badge_count: 0 } },
  setEmail: (email) => set({ email }),
  setEmailVerified: (emailVerified) => set({ emailVerified }),
  setPhone: (phone) => set({ phone }),
  setFirstName: (name) => set({ name }),
  setLastName: (lastName) => set({ lastName }),
  setAuthInitialized: (authInitialized) => set({ authInitialized }),
  setUserDetails: ({
    email,
    name,
    lastName,
    phone,
    isAuthenticated,
    userId,
    firebaseUid,
    isAdmin,
    shouldSeeAdminPanel,
  }) =>
    set({ 
      email, 
      name, 
      lastName, 
      phone, 
      isAuthenticated, 
      userId, 
      firebaseUid, 
      isAdmin: isAdmin || false,
      shouldSeeAdminPanel: shouldSeeAdminPanel || false
    }),
  setLessons: (lessons) => set({ lessons }),
  setPasses: (passes) => set({ passes: Array.isArray(passes) ? passes : [] }), // Ensure passes is an array
  setUser: (user) => set({ user }),
  addLesson: (lesson) =>
    set((state) => ({ lessons: [...state.lessons, lesson] })),
  clearUserDetails: () =>
    set({
      email: "",
      name: "",
      lastName: "",
      phone: "",
      isAuthenticated: false,
      userId: "",
      firebaseUid: "",
      isAdmin: false,
      shouldSeeAdminPanel: false,
      lessons: [],
      passes: [],
      user: null,
      roles: [],
      isTrainer: false,
      isExternalTrainer: false,
      trainerLessonTypes: [],
      pendingSignups: { count: 0, total_amount: 0, online: { count: 0, total_amount: 0, lessons: [] }, on_site: { count: 0, total_amount: 0, lessons: [] }, due: { online_count: 0, online_total: 0, badge_count: 0 } },
    }),
  fetchLessons: async () => {
    const { user } = useAuthStore.getState();
    if (!user) return;
    const idToken = await user.getIdToken();
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user/me/lessons`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      set({ lessons: response.data.objects });
    } catch (error) {
      console.error("Failed to fetch lessons", error);
    }
  },
  fetchUserRoles: async () => {
    const { user } = useAuthStore.getState();
    if (!user) return;
    const idToken = await user.getIdToken();
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user/me/roles`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      set({ 
        roles: response.data.roles || [],
        isTrainer: response.data.is_trainer || false,
        isExternalTrainer: response.data.is_external_trainer || false,
        shouldSeeAdminPanel: response.data.should_see_admin_panel || false,
      });
    } catch (error) {
      console.error("Failed to fetch user roles", error);
      set({ roles: [], isTrainer: false, isExternalTrainer: false, shouldSeeAdminPanel: false });
    }
  },
  markPayOnSite: async (lessonId) => {
    const { user } = useAuthStore.getState();
    if (!user) return;
    const idToken = await user.getIdToken();
    await axios.post(
      `${process.env.REACT_APP_BACKEND_ADDRESS}/user_to_lesson/pay-on-site/`,
      { lesson_id: lessonId },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
  },
  fetchPendingSignups: async () => {
    const { user } = useAuthStore.getState();
    if (!user) return;
    const idToken = await user.getIdToken();
    const empty = {
      count: 0,
      total_amount: 0,
      online: { count: 0, total_amount: 0, lessons: [] },
      on_site: { count: 0, total_amount: 0, lessons: [] },
      due: { online_count: 0, online_total: 0, badge_count: 0, has_renewable: false },
    };
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user/me/owed`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      const online = response.data.online || {
        count: 0,
        total_amount: 0,
        lessons: [],
      };
      const on_site = response.data.on_site || {
        count: 0,
        total_amount: 0,
        lessons: [],
      };
      // "Due now" balance that drives the in-app nags (banner + account badge):
      // only lessons that are NOT in the future. Future admin-signups awaiting
      // payment stay payable on /moje-platnosci but must not nudge the user. A
      // missing/invalid lesson_start is treated as due so real debt is never
      // hidden. This applies to BOTH online debt and pay-on-site lessons, since
      // the backend on_site bucket also includes upcoming cash lessons.
      const now = Date.now();
      const isPastOrInvalid = (l) => {
        const start = l.lesson_start ? new Date(l.lesson_start).getTime() : NaN;
        return Number.isNaN(start) || start <= now;
      };
      // Online exception: a future admin-signup whose category the user is a
      // real "karnetowiec" of (their pass expired) DOES nudge — we want to
      // prompt a pass renewal. Non-renewal future signups stay hidden (PR #113).
      const isOnlineDue = (l) => isPastOrInvalid(l) || l.pass_renewable === true;
      const dueOnlineLessons = (online.lessons || []).filter(isOnlineDue);
      const dueOnSiteLessons = (on_site.lessons || []).filter(isPastOrInvalid);
      const due = {
        online_count: dueOnlineLessons.length,
        online_total: dueOnlineLessons.reduce(
          (sum, l) => sum + (l.price || 0),
          0
        ),
        badge_count: dueOnlineLessons.length + dueOnSiteLessons.length,
        // True when at least one due online lesson is a pass-renewal nudge, so
        // the banner can switch to "odnów karnet" messaging.
        has_renewable: dueOnlineLessons.some((l) => l.pass_renewable === true),
      };
      set({
        pendingSignups: {
          count: response.data.total_count || 0,
          total_amount: response.data.total_amount || 0,
          online,
          on_site,
          due,
        },
      });
    } catch (error) {
      console.error("Failed to fetch owed summary", error);
      set({ pendingSignups: empty });
    }
  },
  fetchTrainerLessonTypes: async () => {
    const { user, isTrainer, isExternalTrainer } = useAuthStore.getState();
    if (!user || (!isTrainer && !isExternalTrainer)) return;
    const idToken = await user.getIdToken();
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/trainers/me/lesson-types`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      set({ trainerLessonTypes: response.data.lesson_types || [] });
    } catch (error) {
      console.error("Failed to fetch trainer lesson types", error);
      set({ trainerLessonTypes: [] });
    }
  },
}),
    {
      name: "auth-storage", // unique name for localStorage key
      partialize: (state) => ({
        // Only persist authentication-related fields
        email: state.email,
        name: state.name,
        lastName: state.lastName,
        phone: state.phone,
        isAuthenticated: state.isAuthenticated,
        emailVerified: state.emailVerified,
        userId: state.userId,
        firebaseUid: state.firebaseUid,
        isAdmin: state.isAdmin,
        shouldSeeAdminPanel: state.shouldSeeAdminPanel,
        authInitialized: state.authInitialized,
        roles: state.roles,
        isTrainer: state.isTrainer,
        isExternalTrainer: state.isExternalTrainer,
        trainerLessonTypes: state.trainerLessonTypes,
      }),
    }
  )
);
